import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Descarga» of the market data panel as a screen (`admin.marketData.download`): the price sources the page offers and
 * the quote assets of the chosen one, in one answer. The rows are the ones `MarketDataSourcesService` and
 * `ExchangePairDiscoveryService` build today; the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-market-data-download-jobs-screens.type-assertions.ts` tie them
 * in both directions.
 */

export const marketDataDownloadSourceSchema = z.strictObject({
  slug: z.string(),
  displayName: z.string(),
  selectable: z.boolean(),
  unavailableReason: z
    .enum(['endpoint-disabled', 'no-asset-catalogue', 'missing-credential'])
    .nullable(),
  missingCredentialEnvVar: z.string().nullable(),
})

export const exchangeQuoteAssetSchema = z.strictObject({
  quoteAsset: z.string(),
  pairCount: z.number(),
  isStablecoin: z.boolean(),
})

/**
 * `exchange` is the source the page chose. Without it the composition applies the first selectable one, the same rule
 * as the page (`MarketDataDownloadTab.tsx`).
 */
export const adminMarketDataDownloadQuerySchema = z.strictObject({
  exchange: z.string().trim().min(1).max(64).optional(),
})

export const adminMarketDataDownloadScreen = defineScreen({
  id: 'admin.marketData.download',
  path: '/api/screens/admin/market-data/download',
  access: 'super_admin',
  query: adminMarketDataDownloadQuerySchema,
  // Sections run in series in the order of the keys: `quoteAssets` goes FIRST because it validates the source asked
  // for, so a source that is not configured is a 400 before anything else is read.
  response: z.strictObject({
    quoteAssets: screenSection(
      z.strictObject({
        exchange: z.string().nullable(),
        items: z.array(exchangeQuoteAssetSchema),
      }),
    ),
    sources: screenSection(z.array(marketDataDownloadSourceSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.marketData'],
  // p95Ms assumes a warm exchangeInfo cache; cold, the read of Binance can take up to 10 s (see the screens README).
  budget: { requests: 1, statements: 5, bytes: 8_000, p95Ms: 2_500 },
  summary: 'Market data download: the price sources and the quote assets of the chosen one',
})

export type AdminMarketDataDownloadQuery = ScreenQuery<typeof adminMarketDataDownloadScreen>
export type AdminMarketDataDownloadData = ScreenData<typeof adminMarketDataDownloadScreen>
